import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, Settings, CheckCircle, FileText, Tag
} from "lucide-react";

export default function AIEmailSettings() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  // Navigation helpers
  const goToManagePage = () => {
    navigate(`/ai/actors/manage/${actorId}`);
  };

  const goToModelsSettings = () => {
    navigate(`/ai/actors/${actorId}/models-settings`);
  };

  const goToAutomationRules = () => {
    navigate(`/ai/actors/${actorId}/automation-rules`);
  };

  const goToDraftingConditions = () => {
    navigate(`/ai/actors/${actorId}/drafting-conditions`);
  };

  const goToEmailTags = () => {
    navigate(`/ai/actors/${actorId}/email-tags`);
  };

  const goToOperationLogs = () => {
    navigate(`/ai/actors/${actorId}/operation-logs`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/ai/actors/manage/${actorId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Manage
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">AI Email Settings</h1>
            <p className="text-sm text-muted-foreground">
              All email settings have been reorganized for better access
            </p>
          </div>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Settings Relocated
            </CardTitle>
            <CardDescription className="text-slate-700">
              All email configuration options are now available from the Manage AI Actor page under the Email category.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-4">
                Quick access to email settings:
              </p>
              
              <div className="grid gap-3">
                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3 px-4"
                  onClick={goToModelsSettings}
                >
                  <Settings className="w-4 h-4 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Models & Settings</div>
                    <div className="text-xs text-muted-foreground">Configure AI models for email</div>
                  </div>
                </Button>

                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3 px-4"
                  onClick={goToAutomationRules}
                >
                  <CheckCircle className="w-4 h-4 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Automation Rules</div>
                    <div className="text-xs text-muted-foreground">Create email automation rules</div>
                  </div>
                </Button>

                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3 px-4"
                  onClick={goToDraftingConditions}
                >
                  <FileText className="w-4 h-4 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Drafting Conditions</div>
                    <div className="text-xs text-muted-foreground">Custom rules for AI email drafts</div>
                  </div>
                </Button>

                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3 px-4"
                  onClick={goToEmailTags}
                >
                  <Tag className="w-4 h-4 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Email Tags</div>
                    <div className="text-xs text-muted-foreground">View all email tags</div>
                  </div>
                </Button>

                <Button 
                  variant="outline" 
                  className="justify-start h-auto py-3 px-4"
                  onClick={goToOperationLogs}
                >
                  <FileText className="w-4 h-4 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <div className="font-medium">Operation Logs</div>
                    <div className="text-xs text-muted-foreground">View AI operation history</div>
                  </div>
                </Button>
              </div>

              <div className="mt-6 pt-4 border-t">
                <Button 
                  onClick={goToManagePage}
                  className="w-full"
                >
                  Go to Manage AI Actor Page
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
